import { readBody } from "@/lib/api/parseResponse";

/**
 * Shared client-side fetch wrapper for proxy (/api/django/...) calls.
 *
 * Non-throwing: returns the raw Response plus the already-parsed body so
 * callers can either branch on res.ok themselves or layer throwIfError() on
 * top. This deliberately mirrors the contract callers need — the quiz
 * controller inspects raw status/body for session-missing retry logic;
 * postEnquiry and authFetch layer throwIfError() on top.
 *
 * Header defaults:
 *   - Accept: application/json  (always)
 *   - Content-Type: application/json  (only when init.body is truthy)
 *   - credentials: "include"  (always; init can override)
 *   - Caller-supplied headers are merged last and take precedence.
 *
 * Every request carries a default timeout: a fetch left in flight when iOS
 * Safari suspends the tab can otherwise stay pending forever after resume,
 * leaving busy/submitting state stuck with nothing to abort it.
 *
 * Must stay above the proxy's own UPSTREAM_TIMEOUT_MS (25s, see
 * app/api/django/[...path]/route.ts). The proxy returns a friendly JSON 504
 * ("The server took too long to respond...") when Django is genuinely slow —
 * but only if the client is still waiting to see it. At the previous 15s
 * value the client always aborted first, so that message was unreachable and
 * every slow-Django case instead fell through to the generic
 * "Something went wrong" copy in mapApiErrorToForm (lib/api/formErrors.ts).
 */
const DEFAULT_TIMEOUT_MS = 30_000;

function withTimeout(callerSignal: AbortSignal | null | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
  if (!callerSignal) return timeout;
  // AbortSignal.any is missing on iOS Safari < 17.4 — there, honor the caller's
  // signal and forgo the timeout rather than dropping the caller's abort.
  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any([callerSignal, timeout]);
  }
  return callerSignal;
}

export async function clientFetch(
  path: string,
  init?: RequestInit,
): Promise<{ res: Response; body: unknown }> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    signal: withTimeout(init?.signal),
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const body = await readBody(res);
  return { res, body };
}
